import { CollectDataConfig, CollectDataStep } from 'src/interfaces/collectData'
import { State } from '../reducers/types'
import {
  clientSortSteps,
  backendSortSteps,
  sortByDisabled,
  sortByCreated,
} from 'src/utils/collectData'
import {
  CollectDataConfigStatus,
  CollectDataStepStatus,
  CollectDataStepType,
} from 'src/constants/collect-data/enums'
import { COLLECT_DATA_STEP_TIME_TO_COMPLETE } from 'src/constants/collect-data/constants'

const isCollectedDataConfigStatusCreated = (collectDataConfig: CollectDataConfig) => {
  return (
    collectDataConfig.status === CollectDataConfigStatus.Created &&
    collectDataConfig.collectDataSteps.some((item) => item.status === CollectDataStepStatus.Created)
  )
}

export const getCollectDataStepConfigs = (state: State): CollectDataConfig[] => {
  return sortByDisabled(
    state.driver.driver?.collectDataStepsConfigs
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        const steps = item.collectDataSteps

        return {
          ...item,
          status: steps.every(
            (item) =>
              item.status !== CollectDataStepStatus.Created &&
              item.status !== CollectDataStepStatus.Failed,
          )
            ? CollectDataConfigStatus.Completed
            : item.status,
          collectDataSteps: sortByCreated(
            steps.every((item) => item.order === 0)
              ? clientSortSteps(steps)
              : backendSortSteps(steps),
          ),
        }
      }) || [],
  )
}

export const getCurrentCollectDataStepConfig = (state: State): CollectDataConfig | null => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.find(isCollectedDataConfigStatusCreated) || null
}

export const getCurrentCollectDataStepConfigIndex = (state: State): number => {
  const stepConfigs = getCollectDataStepConfigs(state)

  const index = stepConfigs.findIndex(isCollectedDataConfigStatusCreated)

  return index >= 0 ? index + 1 : 0
}

export const getCollectDataSteps = (state: State): CollectDataStep[] =>
  getCollectDataStepConfigs(state)
    .map((item) => item.collectDataSteps)
    .flat()
    .filter((item) => item.status !== CollectDataStepStatus.Archived) || []

export const getCurrentCollectDataStep = (state: State): CollectDataStep | null => {
  const steps = getCollectDataSteps(state)

  return steps.find((item) => item.status === CollectDataStepStatus.Created) || null
}

export const getOnlyDelayedStepsLeft = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return (
    stepConfigs.every(
      (item) =>
        item.status !== CollectDataConfigStatus.Created ||
        (item.disabled === true && isCollectedDataConfigStatusCreated(item)),
    ) && stepConfigs.some((item) => isCollectedDataConfigStatusCreated(item))
  )
}

export const getCollectDataStepsRemainingTimeToComplete = (state: State): number => {
  const steps = getCollectDataSteps(state)

  return steps.reduce(
    (res, item) =>
      (res +=
        (item.status === CollectDataStepStatus.Created &&
          COLLECT_DATA_STEP_TIME_TO_COMPLETE[item.type]) ||
        0),
    0,
  )
}

export const getCollectDataStepIndex =
  (stepId: string | undefined) =>
  (state: State): number => {
    const steps = getCollectDataSteps(state)
    const index = steps.findIndex((item) => item.id === stepId)

    return index >= 0 ? index + 1 : 0
  }

export const getCollectDataStepIndexInConfig =
  (stepId: string | undefined) =>
  (state: State): number => {
    const stepConfigs = getCollectDataStepConfigs(state)

    let res = 0
    stepConfigs.find((item) => {
      const stepIndex = item.collectDataSteps.findIndex((step) => step.id === stepId)

      if (stepIndex >= 0) {
        res = stepIndex + 1
      }
    })

    return res
  }

export const getCollectDataStepsCount = (state: State): number => getCollectDataSteps(state).length

export const getStepByType =
  <T>(stepType: CollectDataStepType) =>
  (state: State): T | null => {
    const steps = getCollectDataSteps(state)

    return (steps.find((item) => item.type === stepType) as T) || null
  }

export const getIsCollectDataStarted = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.some((item) => !isCollectedDataConfigStatusCreated(item))
}

export const getIsCollectDataFinished = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.every((item) => !isCollectedDataConfigStatusCreated(item))
}

export const getIsCollectDataFailed = (state: State): boolean => {
  const stepConfigs = getCollectDataStepConfigs(state)

  return stepConfigs.some((item) => item.status === CollectDataConfigStatus.Failed)
}
